import type { Scope, SeverityLevel } from '@sentry/react';

interface SentryLogTags {
  errorBoundary?: string;
  isVersionMismatchModalOpen?: boolean;
}

interface LogOptions {
  fingerprint?: [string] | string;
  tags?: SentryLogTags;
}

const { Sentry } = window;

const addToScope = (scope: Scope, extra: unknown, options: LogOptions = {}) => {
  if (extra) {
    if (typeof extra !== 'object') {
      scope.setExtra('args', [extra]);
    } else {
      scope.setExtra('extra', extra);
    }
  }

  if (options.fingerprint) {
    const fingerprint = Array.isArray(options.fingerprint)
      ? options.fingerprint
      : [options.fingerprint];
    scope.setFingerprint(['{{ default }}', ...fingerprint]);
  }

  if (options.tags) {
    scope.setTags({ ...options.tags });
  }
};

const createLogMethod =
  <T = Error | string>(level?: SeverityLevel) =>
  (errOrMessage: T, extra?: unknown, options?: LogOptions) => {
    if (Sentry?.isEnabled) {
      Sentry.withScope((scope) => {
        addToScope(scope, extra, options);

        if (level) {
          scope.setLevel(level);
        }

        if (typeof errOrMessage === 'string') {
          Sentry.captureMessage(errOrMessage);
        } else {
          Sentry.captureException(errOrMessage);
        }
      });
    } else {
      const consoleMethod =
        level && ['fatal', 'error'].includes(level) ? 'error' : 'warn';

      // eslint-disable-next-line no-console
      console[consoleMethod](errOrMessage, extra, options || '');
    }
  };

const Logger = {
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reduxMiddleware: () => (next: any) => (action: any) => {
    try {
      return next(action);
    } catch (err) {
      Logger.error(err, {
        action,
        source: 'redux action',
      });
      return undefined;
    }
  },

  fatal: createLogMethod('fatal'),
  error: createLogMethod('error'),
  message: createLogMethod<string>(),
  warn: createLogMethod<string>('warning'),

  setContext: (
    user: {
      id: string;
      name: string;
      email: string;
      title: string;
      type: string;
      createdAt: string;
      sessionStartedAt: string;
    },
    organization: {
      id: string;
      host: string;
      plan?: string;
      createdAt: string;
      subscriptionId?: string;
    },
  ) => {
    if (!Sentry) return;

    const { title, type, ...userData } = user;

    const scope = Sentry.getCurrentScope();
    scope.setUser({ ...userData, userTitle: title, userType: type });
    scope.setTag('org_id', organization.id);
    scope.setContext('organization', organization);
  },
};

export default Logger;
